import { Link } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";
import {
  getFeatured,
  getLatest,
  getHot,
  getLatestNews,
  CATEGORY_META,
} from "@/lib/media";
import SectionHeading from "@/components/base/SectionHeading";
import TitleGrid from "@/components/base/TitleGrid";
import HeroCarousel from "./components/HeroCarousel";
import CategoryEntry from "./components/CategoryEntry";
import RankingList from "./components/RankingList";
import NewsHighlight from "./components/NewsHighlight";
import SiteIntro from "./components/SiteIntro";

export default function Home() {
  usePageMeta({
    title: "西瓜影音 - 高清电影电视剧在线播放平台",
    description:
      "西瓜影音为用户提供丰富的影视资源，涵盖最新电影、电视剧等多种类型，支持在线播放和影视资讯浏览，满足您的观影需求。",
    keywords: "西瓜影音,影视资源,电影播放,电视剧观看,在线视频,影视平台,高清电影,影视聚合",
  });

  const featured = getFeatured();
  const latestMovies = getLatest("movie", 6);
  const latestTv = getLatest("tv", 6);
  const hot = getHot(8);
  const latestAll = getLatest("all", 5);
  const latestNews = getLatestNews(3);

  return (
    <div className="animate-fade-in">
      <h1 className="sr-only">西瓜影音 - 高清电影电视剧在线播放平台</h1>

      <HeroCarousel slides={featured} />

      {/* 分类快捷入口 */}
      <section className="w-full px-4 md:px-8 py-10 md:py-14 bg-background-950">
        <SectionHeading
          title="分类快捷入口"
          subtitle="按类型快速进入，找到你想看的影视内容"
        />
        <CategoryEntry />
      </section>

      {/* 最新电影 */}
      <section className="w-full px-4 md:px-8 py-10 md:py-14 bg-background-900">
        <SectionHeading
          title={`最新${CATEGORY_META.movie.label}推荐`}
          subtitle="新鲜上线的院线佳作，第一时间为你呈现"
          to="/category/movie"
        />
        <TitleGrid items={latestMovies} />
      </section>

      {/* 最新电视剧 */}
      <section className="w-full px-4 md:px-8 py-10 md:py-14 bg-background-950">
        <SectionHeading
          title={`最新${CATEGORY_META.tv.label}推荐`}
          subtitle="热播剧集持续更新，追剧不迷路"
          to="/category/tv"
        />
        <TitleGrid items={latestTv} />
      </section>

      {/* 热门排行榜 */}
      <section className="w-full px-4 md:px-8 py-10 md:py-14 bg-background-900">
        <SectionHeading
          title="热门影视排行榜"
          subtitle="根据站内播放热度实时排序"
          to="/category/movie"
          linkLabel="更多热门"
        />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 md:gap-6">
          <div className="lg:col-span-2">
            <RankingList items={hot} />
          </div>
          <div className="rounded-lg bg-background-800/60 border border-background-800 p-5">
            <h3 className="font-heading text-lg font-semibold text-foreground-100 mb-4 flex items-center gap-2">
              <i className="ri-flashlight-line text-accent-400"></i>
              最新上线
            </h3>
            <div className="space-y-4">
              {latestAll.map((item) => (
                <Link
                  key={item.id}
                  to={`/detail/${item.id}`}
                  className="group flex items-center gap-3"
                >
                  <div className="w-12 h-16 rounded-md overflow-hidden bg-background-800 shrink-0">
                    <img
                      src={item.cover}
                      alt={`${item.title} 封面`}
                      title={`${item.title} 高清在线观看`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm text-foreground-100 truncate group-hover:text-primary-400 transition-colors">
                      {item.title}
                    </p>
                    <p className="text-xs text-foreground-400 mt-1">
                      {item.year} · {item.genres[0]}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
            <Link
              to="/search"
              className="mt-5 flex items-center justify-center gap-1 px-4 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors"
            >
              <i className="ri-search-line"></i>
              搜索更多影视
            </Link>
          </div>
        </div>
      </section>

      {/* 影视资讯精选 */}
      <section className="w-full px-4 md:px-8 py-10 md:py-14 bg-background-950">
        <SectionHeading
          title="影视资讯精选"
          subtitle="了解最新影视动态、行业观察与深度影评"
          to="/news"
        />
        <NewsHighlight items={latestNews} />
      </section>

      {/* 网站简介 */}
      <section className="w-full px-4 md:px-8 py-10 md:py-14 bg-background-900">
        <SiteIntro />
      </section>
    </div>
  );
}